import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { formatMenuInstant } from '../lib/date'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { useToast } from '../ui/ToastProvider'
import MenuGuideButton from '../components/MenuGuideButton'

const emptyForm = () => ({
  templateCode: '',
  templateName: '',
  description: '',
  kakaoVendor: '',
  kakaoTemplate: '',
  kakaoTemplateSolapi: '',
  kakaoTemplatePopbill: '',
  smsTemplate: '',
  fcmTemplate: '',
  requiredVariables: '',
  optionalVariables: '',
  variableSchema: '',
  isActive: true,
})

function rowToForm(row) {
  if (!row) return emptyForm()
  return {
    templateCode: row.templateCode != null ? String(row.templateCode) : '',
    templateName: row.templateName != null ? String(row.templateName) : '',
    description: row.description != null ? String(row.description) : '',
    kakaoVendor: row.kakaoVendor != null ? String(row.kakaoVendor) : '',
    kakaoTemplate: row.kakaoTemplate != null ? String(row.kakaoTemplate) : '',
    kakaoTemplateSolapi: row.kakaoTemplateSolapi != null ? String(row.kakaoTemplateSolapi) : '',
    kakaoTemplatePopbill: row.kakaoTemplatePopbill != null ? String(row.kakaoTemplatePopbill) : '',
    smsTemplate: row.smsTemplate != null ? String(row.smsTemplate) : '',
    fcmTemplate: row.fcmTemplate != null ? String(row.fcmTemplate) : '',
    requiredVariables: row.requiredVariables != null ? String(row.requiredVariables) : '',
    optionalVariables: row.optionalVariables != null ? String(row.optionalVariables) : '',
    variableSchema: row.variableSchema != null ? String(row.variableSchema) : '',
    isActive: row.isActive !== false,
  }
}

export default function OperatorNotificationTemplatesPage() {
  const { show } = useToast()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [items, setItems] = useState([])
  const [q, setQ] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [testingId, setTestingId] = useState(null)
  const [catalogOpen, setCatalogOpen] = useState(false)
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalogBusy, setCatalogBusy] = useState(false)
  const [catalogItems, setCatalogItems] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get('/operator/notification-templates')
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setItems(Array.isArray(payload) ? payload : [])
    } catch (e) {
      setError('알림톡 템플릿 목록을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const filtered = useMemo(() => {
    const qq = String(q || '').trim().toLowerCase()
    const arr = Array.isArray(items) ? items : []
    if (!qq) return arr
    return arr.filter((x) => {
      const hay = `${x?.templateCode || ''} ${x?.templateName || ''} ${x?.description || ''}`.toLowerCase()
      return hay.includes(qq)
    })
  }, [items, q])

  const openCatalog = async () => {
    setCatalogOpen(true)
    setCatalogLoading(true)
    try {
      const res = await api.get('/operator/notification-templates/catalog/source-referenced')
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setCatalogItems(Array.isArray(payload) ? payload : [])
    } catch (e) {
      setCatalogItems([])
      show('소스 참조 코드 목록을 불러오지 못했습니다.', 'error')
    } finally {
      setCatalogLoading(false)
    }
  }

  const closeCatalog = () => {
    setCatalogOpen(false)
  }

  const openCreateWithCatalogCode = (code) => {
    const c = String(code || '').trim()
    closeCatalog()
    setEditingId(null)
    setForm({ ...emptyForm(), templateCode: c })
    setModalOpen(true)
  }

  const openEditFromCatalogDbId = async (dbId) => {
    const id = Number(dbId)
    if (!id) return
    setCatalogBusy(true)
    try {
      const res = await api.get(`/operator/notification-templates/${id}`)
      const data = res?.data?.data ?? res?.data
      if (!data || data.id == null) {
        show('템플릿을 불러오지 못했습니다.', 'error')
        return
      }
      closeCatalog()
      setEditingId(data.id)
      setForm(rowToForm(data))
      setModalOpen(true)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '불러오기에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setCatalogBusy(false)
    }
  }

  const openCreate = () => {
    setEditingId(null)
    setForm(emptyForm())
    setModalOpen(true)
  }

  const openEdit = (row) => {
    setEditingId(row.id)
    setForm(rowToForm(row))
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingId(null)
    setForm(emptyForm())
  }

  const onSave = async () => {
    const code = String(form.templateCode || '').trim()
    const name = String(form.templateName || '').trim()
    if (!editingId && !code) {
      show('템플릿 코드를 입력해 주세요.', 'error')
      return
    }
    if (!name) {
      show('템플릿 이름을 입력해 주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      const body = {
        templateName: name,
        description: String(form.description || '').trim() || null,
        kakaoVendor: String(form.kakaoVendor || '').trim() || null,
        kakaoTemplate: String(form.kakaoTemplate || '').trim() || null,
        kakaoTemplateSolapi: String(form.kakaoTemplateSolapi || '').trim() || null,
        kakaoTemplatePopbill: String(form.kakaoTemplatePopbill || '').trim() || null,
        smsTemplate: String(form.smsTemplate || '').trim() || null,
        fcmTemplate: String(form.fcmTemplate || '').trim() || null,
        requiredVariables: String(form.requiredVariables || '').trim() || null,
        optionalVariables: String(form.optionalVariables || '').trim() || null,
        variableSchema: String(form.variableSchema || '').trim() || null,
        isActive: !!form.isActive,
      }
      if (editingId) {
        await api.put(`/operator/notification-templates/${editingId}`, body)
        show('저장되었습니다.', 'success')
      } else {
        await api.post('/operator/notification-templates', { ...body, templateCode: code })
        show('템플릿이 등록되었습니다.', 'success')
      }
      closeModal()
      await load()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.data?.message || e?.message || '저장에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (row) => {
    const next = !row.isActive
    if (!window.confirm(`"${row.templateCode}" 템플릿을 ${next ? '활성화' : '비활성화'}할까요?`)) return
    setSaving(true)
    try {
      await api.patch(`/operator/notification-templates/${row.id}/active`, { isActive: next })
      show(next ? '활성화되었습니다.' : '비활성화되었습니다.', 'success')
      await load()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '변경에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const sendTest = async (row) => {
    if (!row?.id) return
    if (!window.confirm(`"${row.templateName || row.templateCode}" 템플릿으로 로그인 계정 번호에 테스트 알림톡을 발송할까요?`)) return
    setTestingId(row.id)
    try {
      const res = await api.post(`/operator/notification-templates/${row.id}/test`)
      const data = res?.data?.data ?? res?.data
      const masked = data?.recipientPhoneMasked ? ` (${data.recipientPhoneMasked})` : ''
      show(`테스트 알림톡을 발송했습니다.${masked}`, 'success')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.data?.message || e?.message || '테스트 발송에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setTestingId(null)
    }
  }

  const taClass = 'mt-1 w-full rounded border px-3 py-2 text-sm font-mono min-h-[88px]'

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">템플릿관리 &gt; 알림톡</h1>
            <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white text-slate-800 px-4 py-2 text-sm font-semibold hover:bg-slate-50"
            onClick={() => void openCatalog()}
          >
            템플릿 코드
          </button>
          <button
            type="button"
            className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-800"
            onClick={openCreate}
          >
            새 템플릿
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <input
          className="rounded border px-3 py-2 text-sm w-full sm:max-w-md"
          placeholder="코드·이름·설명 검색"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="button" className="text-sm text-brand-navy underline" onClick={() => void load()}>
          새로고침
        </button>
      </div>

      {loading && <Loading label="불러오는 중…" />}
      {error && <InlineError message={error} />}

      {!loading && !error && (
        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">코드</th>
                <th className="text-left px-3 py-2 font-semibold">이름</th>
                <th className="text-left px-3 py-2 font-semibold">벤더</th>
                <th className="text-center px-3 py-2 font-semibold w-24">활성</th>
                <th className="text-left px-3 py-2 font-semibold">수정일</th>
                <th className="text-right px-3 py-2 font-semibold w-52">동작</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                    등록된 템플릿이 없습니다.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id} className="border-b last:border-b-0 hover:bg-slate-50/80">
                    <td className="px-3 py-2 font-mono text-xs">{row.templateCode}</td>
                    <td className="px-3 py-2">{row.templateName}</td>
                    <td className="px-3 py-2 text-slate-600">{row.kakaoVendor || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={row.isActive ? 'text-green-700 font-medium' : 'text-slate-400'}>
                        {row.isActive ? 'ON' : 'OFF'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                      {formatMenuInstant(row, 'updatedAt')}
                    </td>
                    <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                      <button
                        type="button"
                        className="text-xs underline text-slate-600 disabled:opacity-40"
                        disabled={testingId === row.id}
                        onClick={() => void sendTest(row)}
                      >
                        {testingId === row.id ? '발송 중…' : '테스트'}
                      </button>
                      <button type="button" className="text-brand-navy text-xs underline" onClick={() => openEdit(row)}>
                        편집
                      </button>
                      <button type="button" className="text-xs underline text-slate-600" onClick={() => void toggleActive(row)}>
                        {row.isActive ? '끄기' : '켜기'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {catalogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-labelledby="catalog-title"
          onClick={closeCatalog}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex items-start justify-between gap-4">
              <div>
                <h2 id="catalog-title" className="text-lg font-bold">소스에서 참조하는 템플릿 코드</h2>
                <p className="text-xs text-slate-600 mt-1">
                  백엔드 구현 기준 목록입니다. DB 등록 여부는 조회 시점의{' '}
                  <span className="font-mono">notification_templates</span> 와 대소문자 무시 매칭입니다. 새 알림 발송을 추가하면{' '}
                  <span className="font-mono">NotificationTemplateCodeCatalog</span>도 함께 갱신해 주세요.
                </p>
              </div>
              <button type="button" className="text-sm text-slate-500 hover:text-slate-800" onClick={closeCatalog}>
                닫기
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 pt-2">
              {catalogLoading && <Loading label="불러오는 중…" />}
              {!catalogLoading && (
                <table className="w-full text-sm border rounded-lg overflow-hidden">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold w-[180px]">코드</th>
                      <th className="text-left px-3 py-2 font-semibold">설명</th>
                      <th className="text-left px-3 py-2 font-semibold w-[200px]">참조 위치</th>
                      <th className="text-left px-3 py-2 font-semibold w-[140px]">DB</th>
                      <th className="text-right px-3 py-2 font-semibold w-24">동작</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogItems.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-slate-500">목록이 없습니다.</td>
                      </tr>
                    ) : (
                      catalogItems.map((row) => (
                        <tr key={row.code} className="border-b last:border-b-0 hover:bg-slate-50/80">
                          <td className="px-3 py-2 font-mono text-xs align-top">{row.code}</td>
                          <td className="px-3 py-2 text-slate-700 align-top">{row.description}</td>
                          <td className="px-3 py-2 text-xs text-slate-500 align-top whitespace-pre-wrap">{row.sourceHint}</td>
                          <td className="px-3 py-2 align-top">
                            {row.registered ? (
                              <div>
                                <span className={row.dbActive ? 'text-green-700 font-medium text-xs' : 'text-amber-700 font-medium text-xs'}>
                                  {row.dbActive ? '등록·활성' : '등록·비활성'}
                                </span>
                                {row.dbTemplateName ? (
                                  <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[160px]" title={row.dbTemplateName}>
                                    {row.dbTemplateName}
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs">미등록</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right align-top">
                            {row.registered && row.dbId != null ? (
                              <button
                                type="button"
                                className="text-xs text-brand-navy underline font-medium disabled:opacity-40"
                                disabled={catalogBusy}
                                onClick={() => void openEditFromCatalogDbId(row.dbId)}
                              >
                                편집
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="text-xs text-brand-navy underline font-medium disabled:opacity-40"
                                disabled={catalogBusy}
                                onClick={() => openCreateWithCatalogCode(row.code)}
                              >
                                생성
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" role="dialog">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-bold mb-4">{editingId ? '알림톡 템플릿 편집' : '알림톡 템플릿 등록'}</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600">템플릿 코드</label>
                <input
                  className="mt-1 w-full rounded border px-3 py-2 text-sm font-mono"
                  value={form.templateCode}
                  onChange={(e) => setForm((p) => ({ ...p, templateCode: e.target.value }))}
                  disabled={!!editingId}
                  placeholder="예: CATEGORY_LANDING_ALIMTALK"
                />
                {editingId && <p className="text-[11px] text-slate-500 mt-1">코드는 수정할 수 없습니다.</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">템플릿 이름</label>
                <input
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  value={form.templateName}
                  onChange={(e) => setForm((p) => ({ ...p, templateName: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">설명</label>
                <input
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">kakao 벤더</label>
                <select
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  value={form.kakaoVendor}
                  onChange={(e) => setForm((p) => ({ ...p, kakaoVendor: e.target.value }))}
                >
                  <option value="">(미지정)</option>
                  <option value="solapi">solapi</option>
                  <option value="popbill">popbill</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">kakao_template (레거시)</label>
                <textarea className={taClass} value={form.kakaoTemplate} onChange={(e) => setForm((p) => ({ ...p, kakaoTemplate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">kakao_template_solapi (JSON 또는 templateId 문자열)</label>
                <textarea className={taClass} value={form.kakaoTemplateSolapi} onChange={(e) => setForm((p) => ({ ...p, kakaoTemplateSolapi: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">kakao_template_popbill (JSON)</label>
                <textarea className={taClass} value={form.kakaoTemplatePopbill} onChange={(e) => setForm((p) => ({ ...p, kakaoTemplatePopbill: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">sms_template</label>
                <textarea className={taClass} value={form.smsTemplate} onChange={(e) => setForm((p) => ({ ...p, smsTemplate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">fcm_template</label>
                <textarea className={taClass} value={form.fcmTemplate} onChange={(e) => setForm((p) => ({ ...p, fcmTemplate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">required_variables (JSON)</label>
                <textarea className={taClass} value={form.requiredVariables} onChange={(e) => setForm((p) => ({ ...p, requiredVariables: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">optional_variables (JSON)</label>
                <textarea className={taClass} value={form.optionalVariables} onChange={(e) => setForm((p) => ({ ...p, optionalVariables: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">variable_schema (JSON)</label>
                <textarea className={taClass} value={form.variableSchema} onChange={(e) => setForm((p) => ({ ...p, variableSchema: e.target.value }))} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!form.isActive}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
                활성
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button type="button" className="rounded border px-4 py-2 text-sm" onClick={closeModal} disabled={saving}>
                취소
              </button>
              <button
                type="button"
                className="rounded bg-brand-navy text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
                onClick={() => void onSave()}
                disabled={saving}
              >
                {saving ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
